/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef } from 'react';
import { Form, Col, Row, Button } from 'react-bootstrap';
import { Formik, Field } from 'formik';

import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import * as yup from 'yup';

import { useParams } from 'react-router-dom';
import Loader from '../common/Loader';
import LoaderButton from '../common/LoaderButton';
import FieldWrapper from '../form/wrappers/Field';
import ConfirmModal from '../common/ConfirmModal';

import { UUID_REG_EXP } from '../../constants';
import { adminApi } from '../../api';
import { useAlerts } from '../context';

function CompanyWalletForm({ app, onSave }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const { setAlert } = useAlerts();
  const [confirm, setConfirm] = useState({ show: false, data: undefined });
  const formRef = useRef(null);

  const createCompanyWallet = async (values) => {
    if (values) {
      try {
        // eslint-disable-next-line no-param-reassign
        values = { ...values, business_type: 'public_corporation', naics_code: '5221' };
        await adminApi.createCompanyWallet(id, values);
        setAlert({
          message: t('admin.applications.operationalWallets.form.messages.success'),
          variant: 'success',
        });
        onSave();
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      } finally {
        setConfirm({ ...confirm, show: false, data: undefined });
        formRef.current.setSubmitting(false);
      }
    } else {
      setConfirm({ ...confirm, show: false, data: undefined });
      formRef.current.setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          company_wallet_type: 'customer_reserve_wallet',
          business_name: app.business_name || '',
          business_type: app.business_type || 'Default: Public Corporation',
          naics_code: app.naics_code || 'Default: 5221',
          wallet_uuid: app.wallet_uuid || '',
        }}
        innerRef={formRef}
        validationSchema={yup.object().shape({
          initial_reserve_balance: yup
            .string()
            .trim()
            .required(
              t('common:form.errors.required', {
                field: t(
                  'applications.instantSettlement.form.fields.crwInitialFundingAmount.label'
                ),
              })
            ),

          warning_threshold: yup
            .string()
            .trim()
            .required(
              t('common:form.errors.required', {
                field: t('applications.instantSettlement.form.fields.warningBalance.label'),
              })
            ),

          minimum_balance: yup
            .string()
            .trim()
            .required(
              t('common:form.errors.required', {
                field: t('applications.instantSettlement.form.fields.minimumBalance.label'),
              })
            ),

          business_name: yup.string().required(
            t('common:form.errors.required', {
              field: t('admin.applications.operationalWallets.form.fields.companyLegalName.label'),
            })
          ),
          business_type: yup.string().required(
            t('common:form.errors.required', {
              field: t('admin.applications.operationalWallets.form.fields.businessType.label'),
            })
          ),
          naics_code: yup.string().required(
            t('common:form.errors.required', {
              field: t('admin.applications.operationalWallets.form.fields.naicsCode.label'),
            })
          ),
          wallet_uuid: yup
            .string()
            .required(
              t('common:form.errors.required', {
                field: t('admin.applications.operationalWallets.form.fields.walletId.label'),
              })
            )
            .matches(UUID_REG_EXP, { message: t('common:form.fields.uuid.error') }),
        })}
        onSubmit={(values) => setConfirm({ ...confirm, show: true, data: values })}
      >
        {({ errors, handleSubmit, isSubmitting, dirty }) => (
          <Form className="pb-4" noValidate autoComplete="new-password" onSubmit={handleSubmit}>
            <div className="d-flex align-items-center mb-2">
              <h4>{t('admin.applications.operationalWallets.crwFormTitle')}</h4>
            </div>
            <p className="text-info mb-4">
              {t('admin.applications.operationalWallets.description', {
                wallet: t('admin.applications.operationalWallets.crw'),
              })}
              <Button
                href={`/admin/end_users?app_handle=${app.handle}`}
                variant="link"
                className="p-0 ps-2 text-reset text-underline"
              >
                {t('admin.applications.operationalWallets.endUserLinkText')}
              </Button>
            </p>
            <div className="position-relative">
              {isSubmitting && <Loader />}

              <Row>
                <Col md="12" xl="4" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    className="m-0"
                    tabIndex={0}
                    id="initial_reserve_balance"
                    type="number"
                    name="initial_reserve_balance"
                    label={t(
                      'applications.instantSettlement.form.fields.crwInitialFundingAmount.label'
                    )}
                    component={FieldWrapper}
                  />
                </Col>
                <Col md="12" xl="4" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    className="m-0"
                    tabIndex={0}
                    id="warning_threshold"
                    type="number"
                    name="warning_threshold"
                    label={t('applications.instantSettlement.form.fields.warningBalance.label')}
                    component={FieldWrapper}
                  />
                </Col>
                <Col md="12" xl="4" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    className="m-0"
                    tabIndex={0}
                    id="minimum_balance"
                    type="number"
                    name="minimum_balance"
                    label={t('applications.instantSettlement.form.fields.minimumBalance.label')}
                    component={FieldWrapper}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm="12" lg="6" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    className="m-0"
                    tabIndex={0}
                    id="business_name"
                    name="business_name"
                    label={t(
                      'admin.applications.operationalWallets.form.fields.companyLegalName.label'
                    )}
                    component={FieldWrapper}
                  />
                </Col>
                <Col sm="12" lg="6" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    readOnly
                    className="m-0"
                    tabIndex={0}
                    id="business_type"
                    name="business_type"
                    label={t(
                      'admin.applications.operationalWallets.form.fields.businessType.label'
                    )}
                    component={FieldWrapper}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm="12" lg="6" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    readOnly
                    className="m-0"
                    tabIndex={0}
                    id="naics_code"
                    name="naics_code"
                    label={t('admin.applications.operationalWallets.form.fields.naicsCode.label')}
                    component={FieldWrapper}
                  />
                </Col>
                <Col sm="12" lg="6" className="mb-3 mb-lg-0">
                  <Field
                    floating
                    autofocus
                    required
                    className="m-0"
                    tabIndex={0}
                    id="wallet_uuid"
                    name="wallet_uuid"
                    label={t('admin.applications.operationalWallets.form.fields.walletId.label')}
                    component={FieldWrapper}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-4">
                {dirty && (
                  <Button
                    variant="outline-light"
                    onClick={() => formRef.current?.resetForm()}
                    className="px-4 mx-3"
                  >
                    {t('common:buttons.cancel')}
                  </Button>
                )}
                <LoaderButton
                  loading={isSubmitting}
                  onClick={isSubmitting ? (e) => e.preventDefault() : undefined}
                  type="submit"
                  disabled={!dirty || Object.keys(errors).length}
                >
                  {t('common:buttons.save')}
                </LoaderButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {confirm.show && (
        <ConfirmModal
          show={confirm.show}
          data={confirm.data}
          title={t('admin.applications.operationalWallets.modal.title')}
          message={t('admin.applications.operationalWallets.modal.description', {
            name: app.nickname,
          })}
          buttonLabel={t('common:buttons.update')}
          onHide={createCompanyWallet}
        />
      )}
    </>
  );
}

CompanyWalletForm.propTypes = {
  /**
   * The values of the form
   */
  app: PropTypes.object.isRequired,
  /**
   * The function to be called when the application is saved
   */
  onSave: PropTypes.func.isRequired,
};

export default CompanyWalletForm;
